<template>
  <div class="page-row" :class="rowClasses" :style="rowStyle">
    <!-- Drag handle slot for edit mode -->
    <slot name="drag-handle" />
    <!-- Row content (columns) -->
    <slot />
  </div>
</template>

<script>
export default {
  name: 'RowNode',
  props: {
    nodeId: String,
    editMode: Boolean,
    // Gutter size in pixels (space between columns)
    gap: { type: [String, Number], default: 24 },
    // Vertical alignment of columns
    align: {
      type: String,
      default: 'stretch',
      validator: (v) =>
        ['start', 'center', 'end', 'stretch', 'baseline'].includes(v),
    },
    // Horizontal distribution of columns
    justify: {
      type: String,
      default: 'start',
      validator: (v) =>
        [
          'start',
          'center',
          'end',
          'space-between',
          'space-around',
          'space-evenly',
        ].includes(v),
    },
    // Whether columns should wrap to new lines
    wrap: { type: Boolean, default: true },
    // Remove gutters entirely
    noGutters: { type: Boolean, default: false },
    // Custom class
    className: { type: String, default: '' },
  },
  computed: {
    rowClasses() {
      return [
        this.className,
        `page-row--align-${this.align}`,
        `page-row--justify-${this.justify}`,
        { 'page-row--no-wrap': !this.wrap },
        { 'page-row--no-gutters': this.noGutters },
      ];
    },
    rowStyle() {
      const gapValue =
        typeof this.gap === 'number' ? `${this.gap}px` : this.gap;
      return {
        '--page-row-gap': this.noGutters ? '0px' : gapValue,
      };
    },
  },
};
</script>

<style lang="less">
.page-row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;

  // Use negative margin to offset column padding for proper edge alignment
  // This is the classic "negative margin" technique for gutters
  margin-left: calc(var(--page-row-gap, 24px) / -2);
  margin-right: calc(var(--page-row-gap, 24px) / -2);

  // Vertical gap between wrapped rows
  row-gap: var(--page-row-gap, 24px);

  // Alignment variants
  &--align-start {
    align-items: flex-start;
  }
  &--align-center {
    align-items: center;
  }
  &--align-end {
    align-items: flex-end;
  }
  &--align-stretch {
    align-items: stretch;
  }
  &--align-baseline {
    align-items: baseline;
  }

  // Justify variants
  &--justify-start {
    justify-content: flex-start;
  }
  &--justify-center {
    justify-content: center;
  }
  &--justify-end {
    justify-content: flex-end;
  }
  &--justify-space-between {
    justify-content: space-between;
  }
  &--justify-space-around {
    justify-content: space-around;
  }
  &--justify-space-evenly {
    justify-content: space-evenly;
  }

  // No wrap variant
  &--no-wrap {
    flex-wrap: nowrap;
  }

  // No gutters variant
  &--no-gutters {
    margin-left: 0;
    margin-right: 0;
    row-gap: 0;
  }
}

// Make intermediate wrapper elements transparent to flexbox
// This allows the row-column relationship to work through PageNodeWrapper
.page-row {
  > .page-node-wrapper,
  > .page-node-children {
    display: contents;
  }
}

// ==============================================
// EDIT MODE: Flex-aware drag and drop
// ==============================================
// In edit mode, we need SortableJS to have real bounding boxes.
// The vue-draggable (page-node-children) becomes a flex container
// that mimics the row's layout. Wrappers get their sizing from
// CSS custom properties computed in PageNodeWrapper.

.page-renderer--editing .page-row {
  // The vue-draggable container becomes a flex container
  > .page-node-children {
    display: flex;
    flex-wrap: wrap;
    align-items: inherit;
    justify-content: inherit;
    width: calc(100% + var(--page-row-gap, 24px));
    margin-left: calc(var(--page-row-gap, 24px) / -2);
    margin-right: calc(var(--page-row-gap, 24px) / -2);
    row-gap: var(--page-row-gap, 24px);
  }

  // Wrappers are flex items with column padding
  > .page-node-children > .page-node-wrapper {
    display: flex;
    box-sizing: border-box;
    padding-left: calc(var(--page-row-gap, 24px) / 2);
    padding-right: calc(var(--page-row-gap, 24px) / 2);
    min-width: 0;
    // Sizing is handled by .page-node-wrapper--col-edit in PageNodeWrapper.vue
  }

  // Sortable feedback elements
  > .page-node-children > .sortable-ghost {
    opacity: 0.4;
    background: rgba(0, 0, 0, 0.05);
  }
  > .page-node-children > .sortable-drag {
    opacity: 0.8;
  }
}

// No-gutters variant in edit mode
.page-renderer--editing .page-row--no-gutters {
  > .page-node-children {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    row-gap: 0;
  }
  > .page-node-children > .page-node-wrapper {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
